import CalendarMonth from "@mui/icons-material/CalendarMonth";
import ContentCopy from "@mui/icons-material/ContentCopy";
import Delete from "@mui/icons-material/Delete";
import Draw from "@mui/icons-material/Draw";
import EventBusy from "@mui/icons-material/EventBusy";
import ExpandMore from "@mui/icons-material/ExpandMore";
import GroupAdd from "@mui/icons-material/GroupAdd";
import PersonAdd from "@mui/icons-material/PersonAdd";
import Send from "@mui/icons-material/Send";
import SwapHoriz from "@mui/icons-material/SwapHoriz";
import Tune from "@mui/icons-material/Tune";
import { Button, ListItemIcon, ListItemText, Menu, MenuItem, MenuList } from "@mui/material";
import {
  BulkAddReviewersDialog,
  BulkSetSignerDialog,
  BulkActionSubmission,
} from "components/BulkActions";
import { ChangeEncampStatusDialog } from "components/Submission/ChangeEncampStatusDialog";
import { VALID_ENCAMP_STATUSES } from "encamp-shared/src/submissions";
import { Frequency, SubmissionStatus } from "generated-graphql/graphql";
import { useGridSelection } from "hooks/omnisearch-v2";
import { useMemo, useState } from "react";

import { BulkCreateRevisionSubmissionsDialog } from "./BulkCreateRevisionSubmissionsDialog";
import { ChangeAssigneeDialog } from "./ChangeAssigneeDialog";
import { ChangeDueDateDialog } from "./ChangeDueDateDialog";
import { ChangeStatusDialog } from "./ChangeStatusDialog";
import { DeleteSubmissionsConfirmationDialog } from "./DeleteSubmissionsConfirmationDialog";
import { ExcludeOccurrencesConfirmationDialog } from "./ExcludeOccurrencesConfirmationDialog";
import { SendToReviewersDialog } from "./SendToReviewersDialog";
import { Row } from "./types";
import { partitionSubmissions } from "./utils";

/**
 * Transforms Submissions grid Row to common BulkActionSubmission format.
 */
function toBulkActionSubmission(row: Row): BulkActionSubmission {
  return {
    id: row.id,
    submissionId: row.submission?.id ?? null,
    isVirtualEvent: row.frequency !== Frequency.Once,
    status: row.submission?.status ?? null,
    dueDate: row.deadline ?? null,
    facilityName: row.facilities?.map((f: { name: string }) => f.name).join(", ") || "",
    submissionType: row.submission?.submissionType ?? null,
    title: row.title ?? null,
    reviewerCount: row.submission?.reviewers?.length ?? 0,
  };
}

/**
 * Bulk actions menu for waste submissions.
 *
 * Uses useGridSelection() hook to subscribe to selection state from context.
 * Only THIS component re-renders when selection changes - parent is unaffected.
 */
export function SubmissionBulkActionsMenu() {
  // Subscribe to selection changes via context
  // Only THIS component re-renders when selection changes - parent is unaffected
  const { selectedCount, getSelectedRows, clearSelection } = useGridSelection<Row>();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // Store submissions when a dialog opens - read lazily from getter
  const [submissions, setSubmissions] = useState<Row[]>([]);
  const [showChangeAssigneeDialog, setShowChangeAssigneeDialog] = useState(false);
  const [showChangeDueDateDialog, setShowChangeDueDateDialog] = useState(false);
  const [showChangeStatusDialog, setShowChangeStatusDialog] = useState(false);
  const [showSendToReviewersDialog, setShowSendToReviewersDialog] = useState(false);
  const [showDeleteSubmissionsConfirmationDialog, setShowDeleteSubmissionsConfirmationDialog] =
    useState(false);
  const [showExcludeOccurrencesDialog, setShowExcludeOccurrencesDialog] = useState(false);
  const [showBulkAddReviewersDialog, setShowBulkAddReviewersDialog] = useState(false);
  const [showBulkSetSignerDialog, setShowBulkSetSignerDialog] = useState(false);
  const [showChangeEncampStatusDialog, setShowChangeEncampStatusDialog] = useState(false);
  const [showCreateRevisionsDialog, setShowCreateRevisionsDialog] = useState(false);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    // Snapshot the selection so menu-item guards reflect the live selection
    setSubmissions(getSelectedRows());
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Helper to read submissions and open a dialog
  const openDialogWithSubmissions = (setShowDialog: (show: boolean) => void) => {
    handleClose();
    // Read submissions lazily when dialog opens
    setSubmissions(getSelectedRows());
    setShowDialog(true);
  };

  const handleOpenChangeAssignee = () => {
    openDialogWithSubmissions(setShowChangeAssigneeDialog);
  };

  const handleOpenChangeDueDate = () => {
    openDialogWithSubmissions(setShowChangeDueDateDialog);
  };

  const handleOpenChangeStatus = () => {
    openDialogWithSubmissions(setShowChangeStatusDialog);
  };

  const handleOpenSendToReviewers = () => {
    openDialogWithSubmissions(setShowSendToReviewersDialog);
  };

  const handleOpenDeleteSubmissionsConfirmation = () => {
    openDialogWithSubmissions(setShowDeleteSubmissionsConfirmationDialog);
  };

  const handleOpenExcludeOccurrences = () => {
    openDialogWithSubmissions(setShowExcludeOccurrencesDialog);
  };

  const handleOpenBulkAddReviewers = () => {
    handleClose();
    setShowBulkAddReviewersDialog(true);
  };

  const handleOpenBulkSetSigner = () => {
    handleClose();
    setShowBulkSetSignerDialog(true);
  };

  const handleOpenCreateRevisions = () => {
    handleClose();
    setShowCreateRevisionsDialog(true);
  };

  const { canChangeEncampStatus, encampStatusSubmissions } = useMemo(() => {
    const materialized = submissions.filter(
      (r) => r.submission?.id != null && r.frequency === Frequency.Once
    );
    if (materialized.length === 0) {
      return { canChangeEncampStatus: false, encampStatusSubmissions: [] };
    }
    const statuses = new Set(materialized.map((r) => r.submission?.status));
    if (statuses.size !== 1) {
      return { canChangeEncampStatus: false, encampStatusSubmissions: [] };
    }
    const commonStatus = [...statuses][0];
    const hasValidSubStatuses =
      !!commonStatus && (VALID_ENCAMP_STATUSES[commonStatus]?.length ?? 0) > 0;
    return {
      canChangeEncampStatus: hasValidSubStatuses,
      encampStatusSubmissions: materialized.flatMap((r) => {
        const sub = r.submission;
        if (!sub) return [];
        return [
          { submissionId: sub.id, submissionStatus: sub.status, encampStatus: sub.encampStatus },
        ];
      }),
    };
  }, [submissions]);

  // Only real (materialized) submissions can be deleted; a pure virtual-event
  // selection would be a no-op. Mirror the staff dashboard's Delete guard.
  const hasMaterializedSubmissions = useMemo(
    () => partitionSubmissions(submissions).uniqueSubmissionIds.length > 0,
    [submissions]
  );

  const canCreateRevisions = useMemo(() => {
    const materialized = submissions.filter(
      (r) => r.submission?.id != null && r.frequency === Frequency.Once
    );
    return (
      materialized.length > 0 &&
      materialized.every((r) => r.submission?.status === SubmissionStatus.FilingComplete)
    );
  }, [submissions]);

  const handleOpenChangeEncampStatus = () => {
    handleClose();
    setShowChangeEncampStatusDialog(true);
  };

  const handleSuccess = () => {
    clearSelection();
  };

  return (
    <>
      <Button
        id="bulk-actions-button"
        aria-controls={open ? "bulk-actions-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        endIcon={<ExpandMore />}
        variant="contained"
        color="staff"
        disabled={selectedCount === 0}
        sx={{ textTransform: "uppercase" }}
      >
        Bulk Actions
      </Button>
      <Menu
        id="bulk-actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        // Prevent menu from affecting body scroll, which triggers DataGrid's ResizeObserver
        disableScrollLock
        MenuListProps={{
          "aria-labelledby": "bulk-actions-button",
        }}
      >
        <MenuList>
          <MenuItem onClick={handleOpenChangeStatus}>
            <ListItemIcon>
              <SwapHoriz fontSize="small" />
            </ListItemIcon>
            <ListItemText>Change status...</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleOpenChangeEncampStatus} disabled={!canChangeEncampStatus}>
            <ListItemIcon>
              <Tune fontSize="small" />
            </ListItemIcon>
            <ListItemText>Change Encamp Status...</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleOpenChangeAssignee}>
            <ListItemIcon>
              <PersonAdd fontSize="small" />
            </ListItemIcon>
            <ListItemText>Change assignee(s)...</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleOpenChangeDueDate}>
            <ListItemIcon>
              <CalendarMonth fontSize="small" />
            </ListItemIcon>
            <ListItemText>Change due date...</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleOpenBulkAddReviewers}>
            <ListItemIcon>
              <GroupAdd fontSize="small" />
            </ListItemIcon>
            <ListItemText>Add reviewers...</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleOpenBulkSetSigner}>
            <ListItemIcon>
              <Draw fontSize="small" />
            </ListItemIcon>
            <ListItemText>Set signer...</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleOpenSendToReviewers}>
            <ListItemIcon>
              <Send fontSize="small" />
            </ListItemIcon>
            <ListItemText>Send to reviewers...</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleOpenCreateRevisions} disabled={!canCreateRevisions}>
            <ListItemIcon>
              <ContentCopy fontSize="small" />
            </ListItemIcon>
            <ListItemText>Create Revisions...</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleOpenExcludeOccurrences}>
            <ListItemIcon>
              <EventBusy fontSize="small" />
            </ListItemIcon>
            <ListItemText>Exclude from schedule...</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={handleOpenDeleteSubmissionsConfirmation}
            disabled={!hasMaterializedSubmissions}
          >
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Delete selected submissions..."
              secondary={
                !hasMaterializedSubmissions
                  ? "Only real submissions can be deleted — use Exclude for virtual events"
                  : undefined
              }
            />
          </MenuItem>
        </MenuList>
      </Menu>

      <ChangeStatusDialog
        open={showChangeStatusDialog}
        onClose={() => setShowChangeStatusDialog(false)}
        submissions={submissions}
        onSuccess={handleSuccess}
      />

      <ChangeEncampStatusDialog
        open={showChangeEncampStatusDialog}
        onClose={() => setShowChangeEncampStatusDialog(false)}
        submissions={encampStatusSubmissions}
        onSuccess={handleSuccess}
      />

      <ChangeAssigneeDialog
        open={showChangeAssigneeDialog}
        onClose={() => setShowChangeAssigneeDialog(false)}
        submissions={submissions}
        onSuccess={handleSuccess}
      />

      <ChangeDueDateDialog
        open={showChangeDueDateDialog}
        onClose={() => setShowChangeDueDateDialog(false)}
        submissions={submissions}
        onSuccess={handleSuccess}
      />

      <SendToReviewersDialog
        open={showSendToReviewersDialog}
        onClose={() => setShowSendToReviewersDialog(false)}
        submissions={submissions}
        onSuccess={handleSuccess}
      />

      <DeleteSubmissionsConfirmationDialog
        open={showDeleteSubmissionsConfirmationDialog}
        onClose={() => setShowDeleteSubmissionsConfirmationDialog(false)}
        submissions={submissions}
        onSuccess={handleSuccess}
      />

      <ExcludeOccurrencesConfirmationDialog
        open={showExcludeOccurrencesDialog}
        onClose={() => setShowExcludeOccurrencesDialog(false)}
        submissions={submissions}
        onSuccess={handleSuccess}
      />

      <BulkAddReviewersDialog
        open={showBulkAddReviewersDialog}
        onClose={() => setShowBulkAddReviewersDialog(false)}
        submissions={submissions.map(toBulkActionSubmission)}
        onSuccess={handleSuccess}
        refetchQueries={["SubmissionVirtualEvents"]}
      />

      <BulkSetSignerDialog
        open={showBulkSetSignerDialog}
        onClose={() => setShowBulkSetSignerDialog(false)}
        submissions={submissions.map(toBulkActionSubmission)}
        onSuccess={handleSuccess}
        refetchQueries={["SubmissionVirtualEvents"]}
      />

      <BulkCreateRevisionSubmissionsDialog
        open={showCreateRevisionsDialog}
        onClose={() => setShowCreateRevisionsDialog(false)}
        submissions={submissions.map(toBulkActionSubmission)}
        onSuccess={handleSuccess}
        refetchQueries={["SubmissionVirtualEvents"]}
      />
    </>
  );
}
